import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCode, 
  faDesktop, 
  faCogs,
  faTint,
  faIndustry,
  faMountain,
  faBolt,
  faLightbulb,
  faProjectDiagram,
  faLaptopCode,
  faTasks
} from '@fortawesome/free-solid-svg-icons';
import './CustomSoftware.scss';

/**
 * CustomSoftware component - Displays information about MET Connect®'s custom software solutions
 * 
 * @returns {JSX.Element} The CustomSoftware component
 */
const CustomSoftware = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  /**
   * Handle contact button click - navigate to homepage and scroll to contact section
   */
  const handleContactClick = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  // Features of the custom software solutions
  const features = [
    {
      icon: faProjectDiagram,
      title: "PLC Programming",
      description: "Expert programming for Allen-Bradley, Siemens, Schneider Electric, and other major PLC platforms."
    },
    {
      icon: faDesktop,
      title: "SCADA Development",
      description: "Custom SCADA solutions with intuitive interfaces for real-time monitoring and control of industrial processes."
    },
    {
      icon: faLaptopCode,
      title: "Ladder Logic Programming",
      description: "Industry-standard ladder logic programming with thorough documentation and testing."
    },
    {
      icon: faTasks,
      title: "HMI Design",
      description: "User-friendly human-machine interfaces designed with operator efficiency and clarity in mind."
    },
    {
      icon: faCogs,
      title: "System Integration",
      description: "Seamless integration of software systems with existing hardware and infrastructure."
    },
    {
      icon: faCode,
      title: "Custom Applications",
      description: "Specialized software applications tailored to unique industrial requirements and specifications."
    }
  ];

  // Industry specializations
  const industries = [
    {
      icon: faTint,
      title: "Water Treatment",
      description: "Automated control systems for water treatment facilities including filtration, chemical dosing, and distribution networks."
    },
    {
      icon: faIndustry,
      title: "Mining",
      description: "Robust software solutions for mining operations, including equipment monitoring, safety systems, and process control."
    },
    {
      icon: faMountain,
      title: "Quarrying",
      description: "Specialized control systems for quarrying operations, optimizing extraction and processing workflows."
    },
    {
      icon: faBolt,
      title: "Power Systems",
      description: "Reliable software for power distribution, switchgear control, and electrical system management."
    },
    {
      icon: faLightbulb,
      title: "Power Generation",
      description: "Comprehensive solutions for power generation facilities, including monitoring, control, and safety systems."
    }
  ];

  return (
    <div className="custom-software-page">
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <Container className="hero-content">
          <Row className="align-items-center">
            <Col lg={7} className="hero-text">
              <h1 className="hero-title">
                MET Connect<sup>®</sup> Custom Software Solutions
              </h1>
            </Col>
            <div className="vertical-divider-container">
              <div className="vertical-divider"></div>
            </div>
            <Col>
              <p className="hero-subtitle">
                Specialized software development for industrial automation and control systems, 
                tailored to your specific industry requirements.
              </p>
              <div className="hero-features">
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faCode} className="feature-icon" />
                  <span>Expert PLC & SCADA Programming</span>
                </div>
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faIndustry} className="feature-icon" />
                  <span>Industry-Specific Solutions</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <section className="solution-overview">
        <Container>
          <Row>
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="content-block">
                <h2>Custom Software Solutions</h2>
                <p>
                  MET Connect® provides specialized programming services for industrial automation and control systems,
                  with expertise in PLC programming, SCADA development, and ladder logic across various industrial platforms.
                </p>
                <p>
                  Our team of experienced engineers delivers tailored solutions that optimize process efficiency,
                  improve reliability, and enhance operational visibility across your entire industrial system.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="image-block">
                <img 
                  src={require('../../images/scada-placeholder.jpg')} 
                  alt="Software Engineering Blueprint" 
                  className="img-fluid rounded shadow"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="features-section">
        <Container>
          <h2 className="text-center mb-5">Our Software Development Services</h2>
          <Row>
            {features.map((feature, index) => (
              <Col lg={6} xl={4} key={index} className="mb-4">
                <Card className="feature-card h-100">
                  <Card.Body>
                    <div className="feature-icon">
                      <FontAwesomeIcon icon={feature.icon} />
                    </div>
                    <Card.Title>{feature.title}</Card.Title>
                    <Card.Text>{feature.description}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="industries-section">
        <Container>
          <h2 className="text-center mb-5">Industry Specializations</h2>
          <Row>
            {industries.map((industry, index) => (
              <Col md={6} lg={4} key={index} className="mb-4">
                <Card className="industry-card h-100">
                  <Card.Body>
                    <div className="industry-icon">
                      <FontAwesomeIcon icon={industry.icon} />
                    </div>
                    <Card.Title>{industry.title}</Card.Title>
                    <Card.Text>{industry.description}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="development-process">
        <Container>
          <h2 className="text-center mb-5">Our Development Process</h2>
          <Row className="mb-5">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="content-block">
                <h3>Requirements Analysis</h3>
                <p>
                  We begin by thoroughly understanding your operational needs, existing systems, 
                  and desired outcomes. This detailed analysis forms the foundation for all software development.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="content-block">
                <h3>Design & Development</h3>
                <p>
                  Our engineers create comprehensive software designs and implement robust solutions 
                  using industry-standard programming practices and well-documented code.
                </p>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="content-block">
                <h3>Testing & Quality Assurance</h3>
                <p>
                  All software undergoes rigorous testing to ensure reliability, performance, and 
                  compliance with specifications before deployment in your production environment.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="content-block">
                <h3>Deployment & Support</h3>
                <p>
                  We provide professional installation services and ongoing support to ensure your 
                  systems continue to operate optimally throughout their lifecycle.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="cta-section">
        <Container className="text-center">
          <h3>Ready to Discuss Your Custom Software Needs?</h3>
          <p>
            Contact our team today to discuss how our custom software solutions can improve 
            your industrial processes and operational efficiency.
          </p>
          <Button className="btn btn-primary btn-lg" onClick={handleContactClick}>
            Contact Us Today
          </Button>
        </Container>
      </section>
    </div>
  );
};

export default CustomSoftware;